import { Component, DestroyRef, effect, inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, switchMap } from 'rxjs';
import { ExtensionTypes } from 'src/app/models/centrex/enums/extension-types';
import { Extension } from 'src/app/models/centrex/extension';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { RegistrationStates } from 'src/app/models/centrex/enums/registration-states';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-extensions',
    templateUrl: './extensions.component.html',
    styleUrls: ['./extensions.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatSelect,
        MatOption,
        MatSelectTrigger,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
    ]
})
export class ExtensionsComponent {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['number', 'name', 'type', 'state', 'actions'];
    dataSource: MatTableDataSource<Extension>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    extensionTypes = Object.keys(ExtensionTypes);
    extensionStates = Object.keys(RegistrationStates);
    registrationStates = RegistrationStates;

    filtersForm: UntypedFormGroup;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private extensionService: ExtensionService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.filtersForm = new UntypedFormGroup({});
        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des extensions
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            name: [''],
            number: [''],
            type: [''],
            state: [''],
        });
    }

    loadDatas() {
        this.spinner.show();
        this.extensionService
            .getExtensions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (extensions) => {
                    this.dataSource = new MatTableDataSource(extensions);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Extension, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));

            matchFilter.push(data.name && data.name.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            matchFilter.push(data.number && data.number.trim().toLowerCase().includes(filterArray[2].trim().toLowerCase()));

            const extensiontypes = filterArray[3] ? filterArray[3].split(',') : null;

            if (extensiontypes) {
                matchFilter.push(data.type && extensiontypes.includes(data.type.trim().toLowerCase()));
            }

            const extensionstates = filterArray[4] ? filterArray[4].split(',') : null;

            if (extensionstates) {
                matchFilter.push(data.registrationState && extensionstates.includes(data.registrationState.trim().toLowerCase()));
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['number', 'name', 'type', 'state', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des extensions
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value;
        const name = this.filtersForm.get('name').value;
        const number = this.filtersForm.get('number').value;
        const type = this.filtersForm.get('type').value;
        const state = this.filtersForm.get('state').value;

        const filterValue = (!!id ? id : '') + '¤' + (!!name ? name : '') + '¤' + (!!number ? number : '') + '¤' + (!!type ? type : '') + '¤' + (!!state ? state : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(extensionId: string, extensionName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.ExtensionConfirmMessage', {
            extensionName: extensionName,
        });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.extensionService.deleteExtension(extensionId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        this.translate.instant('Extensions.SuccessMessageDeleteExtension', {
                            extensionName: extensionName,
                        })
                    );
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('Extensions.ErrorMessageDeleteExtension', {
                            extensionName: extensionName,
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }
}
